<template>
  <div
    v-bind="$data"
    class="e-dropdown"
    :class="[{ disabled, 'has-error': errorMsg, [`theme-${theme}`]: theme }]"
    tabindex="-1"
    @blur="blur"
    ref="rootEl"
  >
    <div class="e-dropdown__title" v-html="title" />
    <div
      class="e-dropdown__label"
      :label="`${label}${required ? '*' : ''}`"
      v-html="`${label}${required ? '*' : ''}`"
      v-if="label"
    />
    <div class="e-dropdown__main" :class="[{ active: deviceType.includes('desktop') && active }]">
      <div class="e-dropdown__selection" @click="toggle">
        <div class="e-dropdown__text" v-if="selectedOption" title="selectedOption.text">
          {{ selectedOption.text }}
        </div>
        <div class="e-dropdown__placeholder" v-else>{{ placeholder }}</div>
        <icon
          class="e-dropdown__clear"
          name="close"
          @click="clear"
          v-if="selectedOption?.text && clearable"
        />
        <Icon class="e-dropdown__custom" :svg="dropIcon" v-else-if="dropIcon" />
        <icon class="e-dropdown__arrow" name="down" v-else />
      </div>
      <transition name="options-ani">
        <div class="e-dropdown__options-wrapper" v-if="deviceType.includes('desktop') && active">
          <div class="e-dropdown__search" v-if="showSearch">
            <div class="e-dropdown__search-control">
              <icon name="search" />
              <input
                type="text"
                autocomplete="newPassword"
                v-model="keyword"
                ref="searchEl"
                @blur="onSearchBlur"
              />
            </div>
          </div>
          <div class="e-dropdown__options">
            <!-- <div class="e-dropdown__option placeholder" @click="clearSelection" v-if="clearable && !$isNoE(selectedOption?.code)">{{ $ifEmpty(clearText, placeholder) }}</div> -->
            <div
              class="e-dropdown__option"
              :class="{ selected: option?.code === selectedOption?.code }"
              v-for="(option, index) in filteredOptions"
              :key="index"
              @click="(e) => onOptionClick(e, option)"
            >
              <slot name="option" v-if="$slots.option" :option="option" />
              <div v-else>{{ option.text }}</div>
            </div>
          </div>
        </div>
      </transition>
    </div>
    <div class="e-dropdown__desc" v-if="!$isNoE(description)" v-html="description" />
    <div class="e-dropdown__error-box">
      <div class="e-dropdown__error-msg" v-if="errorMsg">
        <span class="e-dropdown__msg">{{ errorMsg }}</span>
        <icon name="important" size="tiny" />
      </div>
    </div>
    <modal
      class="e-dropdown__options-modal"
      ref="optionsModal"
      @closed="onModalClosed"
      animation="bottom-slide-in"
      :fire-gtm="false"
      closable
    >
      <div class="e-dropdown__options-modal__title">
        <div class="e-dropdown__options-modal__label">{{ label }}</div>
      </div>
      <div class="e-dropdown__options-modal__search" v-if="showSearch">
        <div class="e-dropdown__options-modal__search-control">
          <icon name="search" />
          <input type="text" v-model="keyword" />
        </div>
      </div>
      <div class="e-dropdown__options-modal__content">
        <div
          class="e-dropdown__options-modal__item placeholder"
          @click="clearSelection"
          v-if="clearable && !$isNoE(selectedOption?.code)"
        >
          <div class="e-dropdown__options-modal__option">
            {{ $ifEmpty(clearText, placeholder) }}
          </div>
        </div>
        <div
          class="e-dropdown__options-modal__item"
          :class="{ selected: option?.code === selectedOption?.code }"
          v-for="(option, index) in filteredOptions"
          :key="index"
          @click="(e) => onOptionClick(e, option)"
        >
          <slot name="option" v-if="$slots.option" :option="option" />
          <div class="e-dropdown__options-modal__option" v-else>{{ option.text }}</div>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { computed, defineComponent, nextTick, reactive, toRefs, watch } from 'vue';
import useDevice from '@/hooks/useDevice';
import { isNoW } from '@/utils/obj-utils';

export default defineComponent({
  name: 'Dropdown',
  emits: ['blur', 'change'],
  props: {
    title: {
      type: String,
    },
    label: {
      type: String,
    },
    placeholder: {
      type: String,
    },
    description: {
      type: String,
    },
    options: {
      type: Array,
    },
    selectedOption: {
      type: Object,
    },
    errorMsg: {
      type: String,
    },
    required: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    clearText: {
      type: String,
    },
    hideSelected: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    theme: {
      type: String,
      default: null,
    },
    dropIcon: {
      type: String,
      default: null,
    },
  },
  setup(props, ctx) {
    let blurTimeout = null;
    const { deviceState } = useDevice();
    const state = reactive({
      rootEl: null,
      keyword: null,
      active: false,
      contentEl: null,
      optionsModal: null,
      searchEl: null,
    });
    const computes = {
      filteredOptions: computed(() => {
        let opts = props.options;
        if (props.hideSelected) {
          opts = opts.filter((x) => x.code !== props.selectedOption?.code);
        }
        if (!isNoW(state.keyword)) {
          opts = opts.filter((x) => new RegExp(`^${state.keyword}`, 'gi').test(x.text));
        }
        return opts;
      }),
      showSearch: computed(() => props.options?.length > 10),
    };
    watch(
      () => state.active,
      () => {
        methods.checkMobileMotion();
      }
    );
    watch(
      () => deviceState.deviceType,
      () => {
        methods.checkMobileMotion();
      }
    );
    const methods = {
      onSearchBlur() {
        state.active = false;
      },
      blur(e) {
        if (blurTimeout) {
          clearTimeout(blurTimeout);
        }
        blurTimeout = setTimeout(() => {
          if (document.activeElement === state.searchEl) return;
          if (deviceState.deviceType.includes('desktop')) {
            state.active = false;
            ctx.emit('blur');
          }
        }, 50);
      },
      toggle() {
        if (props.disabled) return;
        state.active = !state.active;
      },
      clear(e) {
        ctx.emit('change', e, null);
      },
      async checkMobileMotion() {
        if (!deviceState.deviceType.includes('desktop')) {
          if (state.active) {
            state.optionsModal.open();
            await nextTick();
          } else {
            state.optionsModal.close();
          }
        } else {
          if (state.optionsModal) {
            state.optionsModal.close();
          }
        }
      },
      onOptionClick(e, option) {
        ctx.emit('change', e, option);
        state.active = false;
      },
      clearSelection(e) {
        ctx.emit('change', e, null);
        state.active = false;
      },
      onModalClosed() {
        state.active = false;
      },
    };
    return {
      ...toRefs(state),
      ...toRefs(deviceState),
      ...computes,
      ...methods,
    };
  },
});
</script>

<style lang="scss">
@import '../styles/variables';
@import '../styles/functions';
@import '../styles/mixins';
.e-dropdown {
  font-size: 14px;
  $c: &;
  position: relative;
  &__title {
    margin-bottom: 16px;
    font-size: 26px;
    font-weight: 500;
    &:empty {
      display: none;
    }
  }
  &__label {
    margin-bottom: 4px;
    &:empty {
      display: none;
    }
  }
  &__placeholder {
    @include h9;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    color: $grey;
  }
  &__main {
    position: relative;
    &.active {
      z-index: 3;
      #{$c}__selection {
        border-color: #828282;
        .e-dropdown__arrow {
          transform: rotate(180deg);
        }
      }
    }
  }
  &__selection {
    height: 56px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
    border: 1px solid #828282;
    border-radius: 2px;
    cursor: pointer;
    padding: 5px 50px 5px 16px;
    tab-index: -1;
  }
  &__text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &__custom {
    position: absolute;
    right: 15px;
    > svg {
      width: 20px;
    }
  }
  &__arrow {
    position: absolute;
    right: 15px;
    > svg {
      width: 20px;
    }
  }
  &__clear {
    position: absolute;
    right: 15px;
    > svg {
      width: 20px;
    }
  }
  &__options-wrapper {
    margin-top: 8px;
    box-shadow: 0 2px 4px rgba($black, 0.5);
    position: absolute;
    z-index: 3;
    width: 100%;
    border: 1px solid $black;
    background-color: $white;
  }
  &__search {
    padding: $space-16;
    background: $grey-light;
  }
  &__search-control {
    background: $white;
    color: $black;
    display: flex;
    padding: $space-4 $space-16;
    > input {
      flex-grow: 1;
      background: transparent;
      border: none !important;
      outline-width: 0 !important;
      padding: 0 !important;
      margin-bottom: 0 !important;
    }
    .e-icon {
      margin-right: $space-16;
      color: $grey;
    }
  }
  &__options {
    max-height: 200px;
    background-color: $white;
    overflow-y: auto;
  }
  &__option {
    height: 55px;
    line-height: 55px;
    background: $white;
    color: $black;
    padding: 0 20px;
    cursor: pointer;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    &.selected {
      background: $blue-sky;
      color: $white;
    }
    &.placeholder {
      background: $white;
      color: $grey;
    }
    &:empty {
      display: none;
    }
    &:hover {
      font-weight: bold;
    }
  }
  &__desc {
    font-size: 12px;
    padding-top: 4px;
    margin-bottom: 4px;
  }
  &__error-box {
    height: 40px;
    padding-top: $space-8;
    text-align: end;
  }
  &__msg {
    margin-top: 1px;
    margin-right: 8px;
  }
  &__error-msg {
    font-size: 12px;
    color: $red;
    display: flex;
    justify-content: flex-start;
  }
  &.has-error {
    #{$c}__selection {
      border-color: $red;
    }
  }
  &.disabled {
    #{$c}__selection {
      border-color: $grey-light;
      background: $grey-light;
      cursor: default;
    }
    #{$c}__arrow {
      display: none;
    }
  }
  &.theme {
    &-search-grey {
      #{$c}__search {
        display: none;
      }
      #{$c}__error-box {
        display: none;
      }
      #{$c}__selection {
        height: 40px;
        color: $black;
      }
      #{$c}__placeholder {
        color: $black;
      }
      #{$c}__options-wrapper {
        min-width: 100%;
        width: auto;
        box-shadow: none;
      }
      #{$c}__options {
        background: $grey-neutral;
        border-color: $grey-neutral;
        max-height: 400px;
        &:after {
          content: '';
          display: block;
          height: 16px;
          border-right: 2px solid $black;
        }
      }
      #{$c}__option {
        background: $grey-neutral;
        color: $white;
        position: relative;
        z-index: 1;
        border-right: 2px solid $black;
        padding: 16px;
        height: auto;
        line-height: 1;
        &:after {
          content: '';
          position: absolute;
          left: 16px;
          bottom: 0;
          width: calc(100% - 32px);
          height: 2px;
          box-shadow: 0 1px 0 0 rgba(255, 255, 255, 0.16) inset;
        }
        &.placeholder {
          display: none;
        }
        &.selected {
          background: $grey-neutral;
          border-right-color: $yellow;
        }
      }
    }
  }
}
.e-dropdown__options-modal {
  align-items: flex-end;
  $m: '.e-dropdown__options-modal';
  .e-modal__content {
    width: 100%;
    background: $white;
    color: $black;
    padding: $space-60 0 0;
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
  }
  .e-modal__close {
    top: 12px;
  }
  &__content {
    max-height: 72px;
    min-height: 40vh;
    overflow: auto;
    padding: 0 22px;
  }
  &__title {
    position: absolute;
    width: 100%;
    top: 0;
    height: 40px;
    text-align: center;
    font-size: 18px;
    line-height: 50px;
  }
  &__search {
    padding: $space-16;
    background: $grey-light;
  }
  &__search-control {
    background: $white;
    color: $black;
    display: flex;
    padding: $space-4 $space-16;
    > input {
      flex-grow: 1;
      background: transparent;
      border: none !important;
      outline-width: 0 !important;
      padding: 0 !important;
      margin-bottom: 0 !important;
    }
    .e-icon {
      margin-right: $space-16;
      color: $grey;
    }
  }
  &__option {
    text-align: center;
    font-size: 16px;
    padding: 14px 20px;
    &:empty {
      display: none;
    }
  }
  &__item {
    &.selected {
      background-color: $blue-sky;
      color: $white;
    }

    &.placeholder {
      background: $white;
      color: $grey;
    }
  }
}
</style>
